import {InfiniteData, QueryClient, useInfiniteQuery, useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {jsonInit} from './client';
import {useAuth} from '../hooks/useAuth';
import {DeletedPage, FeedFilters, FeedPage, Kind, LatestDefaults, PlantSuggestion, PlantSummary, Post, PostInput} from '../types';

// Tells, plants and the feed. Query keys:
//   ['posts', 'feed', filters]  infinite feed pages
//   ['posts', id]               one tell
//   ['posts', 'deleted']        recently deleted
//   ['plants', 'suggest', q]    autocomplete
//   ['plants']                  every plant with its group and latest tell (the Plants screen and the filter picker)
//   ['posts', 'latest']         prefill for a new tell
// ui/api/plants.ts adds ['plants', id] and ['posts', 'memories', days].

export const normaliseFilters = (f: FeedFilters = {}): Required<FeedFilters> => ({
    kind: f.kind ?? null,
    detail: f.detail ?? null,
    plantId: f.plantId ?? null,
    species: f.species ?? null,
    placeId: f.placeId ?? null,
    happy: f.happy ?? null,
    from: f.from ?? null,
    to: f.to ?? null
});

export const isUnfiltered = (f: FeedFilters = {}): boolean => Object.values(normaliseFilters(f)).every(v => v === null);

export const feedKey = (filters: FeedFilters = {}) => ['posts', 'feed', normaliseFilters(filters)] as const;

const feedParams = (filters: FeedFilters, cursor: string | null): string => {
    const params = new URLSearchParams();
    for (const [k, v] of Object.entries(normaliseFilters(filters))) if (v) params.set(k, v);
    if (cursor) params.set('cursor', cursor);
    return params.toString();
};

export function useFeed(filters: FeedFilters = {}) {
    const {call} = useAuth();
    return useInfiniteQuery({
        queryKey: feedKey(filters),
        queryFn: ({pageParam}) => call<FeedPage>(`/api/posts?${feedParams(filters, pageParam)}`),
        initialPageParam: null as string | null,
        getNextPageParam: page => page.nextCursor,
        staleTime: 60 * 1000
    });
}

/** The tell from any loaded feed page, used to paint the detail instantly. */
function findInFeeds(queryClient: QueryClient, id: string): Post | undefined {
    for (const [, data] of queryClient.getQueriesData<InfiniteData<FeedPage>>({queryKey: ['posts', 'feed']})) {
        const hit = data?.pages.flatMap(p => p.items).find(p => p.id === id);
        if (hit) return hit;
    }
    return undefined;
}

export function usePost(id: string | undefined) {
    const {call} = useAuth();
    const queryClient = useQueryClient();
    return useQuery({
        queryKey: ['posts', id],
        queryFn: () => call<Post>(`/api/posts/${id}`),
        enabled: !!id,
        initialData: () => (id ? findInFeeds(queryClient, id) : undefined),
        initialDataUpdatedAt: () => queryClient.getQueryState(['posts', 'feed'])?.dataUpdatedAt,
        staleTime: 30 * 1000
    });
}

export function useDeletedPosts() {
    const {call} = useAuth();
    return useQuery({queryKey: ['posts', 'deleted'], queryFn: () => call<DeletedPage>('/api/posts/deleted')});
}

/** How often this gardener uses each kind; the picker leads with their usual ones. */
export function useKindCounts() {
    const {call} = useAuth();
    return useQuery({
        queryKey: ['posts', 'kind-counts'],
        queryFn: () => call<{counts: Partial<Record<Kind, number>>}>('/api/posts/kind-counts').then(r => r.counts),
        staleTime: 5 * 60 * 1000
    });
}

export function useLatestDefaults() {
    const {call} = useAuth();
    return useQuery({queryKey: ['posts', 'latest'], queryFn: () => call<LatestDefaults>('/api/posts/latest'), staleTime: 5 * 60 * 1000});
}

export function usePlantSuggest(q: string) {
    const {call} = useAuth();
    return useQuery({
        queryKey: ['plants', 'suggest', q],
        queryFn: () => call<{items: PlantSuggestion[]}>(`/api/plants/suggest?q=${encodeURIComponent(q)}`).then(r => r.items),
        staleTime: 60 * 1000
    });
}

export function usePlants() {
    const {call} = useAuth();
    return useQuery({queryKey: ['plants'], queryFn: () => call<{items: PlantSummary[]}>('/api/plants').then(r => r.items), staleTime: 60 * 1000});
}

/** Puts a tell at the top of the unfiltered feed cache (the card "slides in"). */
export function prependToFeed(queryClient: QueryClient, post: Post) {
    queryClient.setQueryData<InfiniteData<FeedPage>>(feedKey({}), data => {
        if (!data) return data;
        const [first, ...rest] = data.pages;
        return {...data, pages: [{...first, items: [post, ...first.items.filter(p => p.id !== post.id)]}, ...rest]};
    });
}

/** Replaces a tell wherever it is cached. */
export function updateCachedPost(queryClient: QueryClient, post: Post) {
    queryClient.setQueryData(['posts', post.id], post);
    queryClient.setQueriesData<InfiniteData<FeedPage>>({queryKey: ['posts', 'feed']}, data =>
        data ? {...data, pages: data.pages.map(page => ({...page, items: page.items.map(p => (p.id === post.id ? post : p))}))} : data
    );
}

export function removeCachedPost(queryClient: QueryClient, id: string) {
    queryClient.setQueriesData<InfiniteData<FeedPage>>({queryKey: ['posts', 'feed']}, data =>
        data ? {...data, pages: data.pages.map(page => ({...page, items: page.items.filter(p => p.id !== id)}))} : data
    );
}

export function usePostMutations() {
    const {call} = useAuth();
    const queryClient = useQueryClient();
    const invalidateFeeds = () => {
        queryClient.invalidateQueries({queryKey: ['posts', 'feed']});
        return queryClient.invalidateQueries({queryKey: ['posts', 'memories']});
    };

    const create = useMutation({
        mutationFn: (input: PostInput) => call<Post>('/api/posts', jsonInit('POST', input)),
        onSuccess: post => {
            queryClient.setQueryData(['posts', post.id], post);
            prependToFeed(queryClient, post);
            queryClient.invalidateQueries({queryKey: ['posts', 'kind-counts']});
            queryClient.setQueryData<LatestDefaults>(['posts', 'latest'], {placeId: post.place?.id ?? null, plant: post.plants[0]?.label ?? null});
            queryClient.invalidateQueries({queryKey: ['plants']});
            queryClient.invalidateQueries({queryKey: ['places']});
            void invalidateFeeds();
        },
        meta: {silent: true}
    });

    const patch = useMutation({
        mutationFn: ({id, ...input}: Partial<PostInput> & {id: string}) => call<Post>(`/api/posts/${id}`, jsonInit('PATCH', input)),
        onSuccess: post => {
            updateCachedPost(queryClient, post);
            queryClient.invalidateQueries({queryKey: ['plants']});
            queryClient.invalidateQueries({queryKey: ['places']});
            void invalidateFeeds();
        },
        meta: {silent: true}
    });

    const softDelete = useMutation({
        mutationFn: (id: string) => call<void>(`/api/posts/${id}`, {method: 'DELETE'}),
        onMutate: id => removeCachedPost(queryClient, id),
        onError: () => void invalidateFeeds(),
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ['posts', 'deleted']});
            queryClient.invalidateQueries({queryKey: ['places']});
        }
    });

    const restore = useMutation({
        mutationFn: (id: string) => call<Post>(`/api/posts/${id}/restore`, jsonInit('POST')),
        onSuccess: post => {
            queryClient.setQueryData(['posts', post.id], post);
            queryClient.invalidateQueries({queryKey: ['posts', 'deleted']});
            queryClient.invalidateQueries({queryKey: ['places']});
            void invalidateFeeds();
        }
    });

    const purge = useMutation({
        mutationFn: (id: string) => call<void>(`/api/posts/${id}/permanent`, {method: 'DELETE'}),
        onSuccess: (_, id) => {
            queryClient.removeQueries({queryKey: ['posts', id]});
            queryClient.invalidateQueries({queryKey: ['posts', 'deleted']});
        }
    });

    return {create, patch, softDelete, restore, purge};
}

/** Remove and reorder a tell's uploaded photos (uploads themselves go through ui/lib/uploadQueue). */
export function usePhotoMutations() {
    const {call} = useAuth();
    const queryClient = useQueryClient();
    const refresh = (postId: string) => {
        queryClient.invalidateQueries({queryKey: ['posts', postId]});
        queryClient.invalidateQueries({queryKey: ['posts', 'feed']});
    };

    const remove = useMutation({
        mutationFn: ({postId, photoId}: {postId: string; photoId: string}) => call<void>(`/api/posts/${postId}/photos/${photoId}`, {method: 'DELETE'}),
        onMutate: ({postId, photoId}) => {
            const cached = queryClient.getQueryData<Post>(['posts', postId]);
            if (cached) updateCachedPost(queryClient, {...cached, photos: cached.photos.filter(p => p.id !== photoId)});
        },
        onSettled: (_r, _e, {postId}) => refresh(postId)
    });

    const reorder = useMutation({
        mutationFn: ({postId, photoIds}: {postId: string; photoIds: string[]}) => call<void>(`/api/posts/${postId}/photos/order`, jsonInit('PUT', {photoIds})),
        onMutate: ({postId, photoIds}) => {
            const cached = queryClient.getQueryData<Post>(['posts', postId]);
            if (cached) {
                const byId = new Map(cached.photos.map(p => [p.id, p]));
                updateCachedPost(queryClient, {...cached, photos: photoIds.map((id, i) => ({...byId.get(id)!, ordinal: i + 1}))});
            }
        },
        onSettled: (_r, _e, {postId}) => refresh(postId)
    });

    return {remove, reorder};
}
